import {
	Scene,
	OrthographicCamera,
	WebGLRenderer,
	PlaneGeometry,
	MeshBasicMaterial,
	Vector2,
	Vector3,
	SRGBColorSpace,
	RepeatWrapping,
	LinearFilter,
	ClampToEdgeWrapping,
	DoubleSide,
	Mesh,
	Color,
	TextureLoader,
	VideoTexture,
} from 'three';
// import GUI from 'lil-gui';
import { EffectComposer, EffectPass, RenderPass } from 'postprocessing';
// import { CurveEffect } from "./fx/CurveEffect";
import { CurveEffect } from '@/utils/webGLCurvePostEffect.js';
import { eventBus } from '@spon/plugins';
import gsap from 'gsap';
import Tempus from 'tempus';

class MudCurveExperience {
	#canvas;
	#observer;
	#options;
	#meshes;
	#sceneEls;
	#sizes;
	#scene;
	// #gui;
	#camera;
	#renderer;
	composer;
	#curveEffect;
	#canvasPadding;

	#observerOptions = {
		root: null,
		rootMargin: '25% 0px', // Show scene when they are 1/4 viewport height away, less scenes = more performance
		threshold: 0,
	};

	/**
	 * @param {HTMLCanvasElement} canvas - The canvas element
	 * @param {Object} smoother - The smoother object
	 * @param {Object} options - Configuration options
	 * @param {boolean} options.autoStart - Whether to start the experience automatically
	 * @param {string} options.sceneSelector - The selector for the scene elements. Default is "[data-scene]"
	 * @param {string} options.assetSelector - The selector for the asset elements. Default is "[data-scene-asset]"
	 * @param {string} options.fillSelector - The selector for the fill elements. Default is "[data-scene-fill]"
	//  * @param {boolean} options.showGui - Whether to show the GUI
	 * @param {Object} options.uniforms - Initial uniform values
	 * @param {number} options.uniforms.distance - Distance uniform value
	 * @param {number} options.uniforms.strength - Strength uniform value
	 */
	constructor(canvas, options = {}) {
		this.#canvas = canvas;
		this.#options = {
			autoStart: false,
			sceneSelector: '[data-scene]',
			assetSelector:
				'img:not(.video-poster-image__img),video,[data-scene-fill]',
			fillSelector: 'data-scene-fill',
			uniforms: {
				distance: 0,
				strength: 0,
			},
			...options,
		};

		this.#meshes = [];
		this.#sceneEls = [];

		this.#sizes = {
			width: window.innerWidth,
			height: window.innerHeight,
		};

		// const onScroll = scroll => {
		// 	this.#meshes.forEach(mesh => {
		// 		mesh._cachedSpecs = this.#getElementSpecsNormalized(mesh.domElement);
		// 		this.#setMeshSpecs(mesh, ...mesh._cachedSpecs);
		// 	});
		// };

		// eventBus.on('scroll:update', onScroll);

		// this.#sizes = {
		// 	width: window.innerWidth,
		// 	height: window.innerHeight * (1 + this.#canvasPadding * 2),
		// };

		// 404 Page etc
		if (!this.#canvas) return () => {};

		this.#setupScene();
		this.#setupCamera();
		this.#setupRenderer();
		this.#setupPostProcessing();
		this.#setupEventListeners();
		this.#setupIntersectionObserver();

		this._sharedGeometry = new PlaneGeometry(1, 1);
		this._sharedMaterials = {
			image: new MeshBasicMaterial({
				transparent: true,
				side: DoubleSide,
				opacity: 0,
			}),
			fill: new MeshBasicMaterial({
				transparent: true,
				side: DoubleSide,
				opacity: 0,
			}),
			video: new MeshBasicMaterial({
				transparent: true,
				side: DoubleSide,
				opacity: 0,
			}),
		};

		this._scratchVec2 = new Vector2();
		this._scratchVec3 = new Vector3();

		eventBus.on('ExperienceController:update', newScenes => {
			newScenes.forEach(scene => {
				this.#observer.observe(scene);
			});
		});

		if (this.#options.autoStart) {
			this.start();
		}
	}

	/**
	 * setupScene
	 * Setup the scene
	 * @returns {void}
	 * @private
	 */
	#setupScene() {
		this.#scene = new Scene();
		// this.#gui = new GUI();
		// this.#gui.hide();

		// if (this.#options.showGui) {
		// 	this.#gui.show();
		// }
	}

	/**
	 * getOrthographicCameraFrustum
	 * Calculates the camera's left, right, top, and bottom properties for an orthographic camera
	 * based on the browser viewport size and a desired scene size.
	 *
	 * @param {number} sceneSize - The desired size of the scene in Three.js units.
	 * @returns {Object} - An object with the following properties:
	 *   - left: The left edge of the camera's view frustum.
	 *   - right: The right edge of the camera's view frustum.
	 *   - top: The top edge of the camera's view frustum.
	 *   - bottom: The bottom edge of the camera's view frustum.
	 */
	#getOrthographicCameraFrustum(sceneSize) {
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;
		const aspectRatio = viewportWidth / viewportHeight;

		let cameraLeft, cameraRight, cameraTop, cameraBottom;

		if (aspectRatio >= 1) {
			cameraLeft = -sceneSize;
			cameraRight = sceneSize;
			cameraTop = sceneSize / aspectRatio;
			cameraBottom = -sceneSize / aspectRatio;
		} else {
			cameraLeft = -sceneSize * aspectRatio;
			cameraRight = sceneSize * aspectRatio;
			cameraTop = sceneSize;
			cameraBottom = -sceneSize;
		}

		return {
			left: cameraLeft,
			right: cameraRight,
			top: cameraTop,
			bottom: cameraBottom,
		};
	}

	/**
	 * setupCamera
	 * Setup the camera
	 * @returns {void}
	 * @private
	 */
	#setupCamera() {
		const cameraFrustum = this.#getOrthographicCameraFrustum(10);
		this.#camera = new OrthographicCamera(
			cameraFrustum.left,
			cameraFrustum.right,
			cameraFrustum.top,
			cameraFrustum.bottom,
			0.1,
			1000
		);
		this.#camera.position.set(0, 0, 10);
		this.#scene.add(this.#camera);
	}

	/**
	 * setupRenderer
	 * Setup the renderer
	 * @returns {void}
	 * @private
	 */
	#setupRenderer() {
		this.#renderer = new WebGLRenderer({
			canvas: this.#canvas,
			powerPreference: 'high-performance',
			antialias: false,
			stencil: false,
			depth: false,
			alpha: true,
		});
		this.#renderer.setSize(this.#sizes.width, this.#sizes.height);
		this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
	}

	/**
	 * setupPostProcessing
	 * Setup post-processing
	 * @returns {void}
	 * @private
	 */
	#setupPostProcessing() {
		this.composer = new EffectComposer(this.#renderer);
		this.composer.addPass(new RenderPass(this.#scene, this.#camera));
		this.#curveEffect = new CurveEffect({
			distance: this.#options.uniforms.distance,
			strength: this.#options.uniforms.strength,
			// radius: 0.1, // Adjust this value to control corner roundness (0-1)
		});
		this.composer.addPass(new EffectPass(this.#camera, this.#curveEffect));

		// Setup GUI controls
		// this.#gui
		// 	.add(this.#curveEffect.uniforms.get('uDistance'), 'value')
		// 	.min(0)
		// 	.max(100)
		// 	.step(0.01)
		// 	.name('Distance');
		// this.#gui
		// 	.add(this.#curveEffect.uniforms.get('uStrength'), 'value')
		// 	.min(0)
		// 	.max(1)
		// 	.step(0.01)
		// 	.name('Strength');
		// this.#gui
		// 	.add(this.#curveEffect.uniforms.get('uRadius'), 'value')
		// 	.min(0)
		// 	.max(0.5)
		// 	.step(0.01)
		// 	.name('Corner Radius');
	}

	/**
	 * getElementSpecsNormalized
	 * @param {HTMLElement} el
	 * @returns {Array<number>} [x, y, w, h]
	 */
	#getElementSpecsNormalized(el) {
		const rect = el.getBoundingClientRect();
		const xN = ((rect.left + rect.width / 2) / window.innerWidth) * 2 - 1;
		const yN =
			-(
				(rect.top +
					rect.height / 2 +
					window.lenis.scroll -
					window.lenis.scroll) /
				window.innerHeight
			) *
				2 +
			1;

		// reuse _scratchVec3
		this._scratchVec3.set(xN, yN, 0).unproject(this.#camera);
		const { x, y } = this._scratchVec3;

		// width/height normalized also
		const w = (rect.width / window.innerWidth) * 2 + 1;
		const h = (rect.height / window.innerHeight) * 2 + 1;
		return [x, y, w, h];

		// const elRect = el.getBoundingClientRect();
		// const elCenter = {
		// 	x: elRect.left + elRect.width / 2,
		// 	y: elRect.top + elRect.height / 2 + window.lenis.scroll,
		// };
		// const _x = (elCenter.x / window.innerWidth) * 2 - 1;
		// const _y =
		// 	-(
		// 		(elCenter.y - window.lenis.scroll) /
		// 		(this.getLVH() * (1 + this.#canvasPadding * 2))
		// 	) *
		// 		2 +
		// 	1;
		// const w = (elRect.width / window.innerWidth) * 2 + 1;
		// const h =
		// 	(elRect.height / (this.getLVH() * (1 + this.#canvasPadding * 2))) * 2 + 1;

		// const elVecWorld = new THREE.Vector3(_x, _y, 0).unproject(this.#camera);
		// const { x, y } = elVecWorld;

		// return [x, y, w, h];
	}

	/**
	 * setMeshSpecs
	 * @param {Object} mesh
	 * @param {number} x
	 * @param {number} y
	 * @param {number} w
	 * @param {number} h
	 * @returns {void}
	 * @private
	 */
	#setMeshSpecs(mesh, x, y, w, h) {
		const { width, height } = this.#getCanvasSizeInThreeUnits();

		mesh.current.position.x = x;
		mesh.current.position.y = y;

		const scaled = (m, n) => (m * (1 - Math.abs(n))) / 2;
		let sw = scaled(width, w);
		let sh = scaled(height, h);

		// log(mesh, x, y, w, h, width, height, sw, sh);

		mesh.current.scale.x = sw;
		mesh.current.scale.y = sh;
	}

	/**
	 * getCanvasSizeInThreeUnits
	 * @returns {{width: number, height: number}}
	 * @private
	 */
	#getCanvasSizeInThreeUnits() {
		const canvasSize = new Vector2();
		this.#renderer.getSize(canvasSize);

		let canvasWidthInUnits, canvasHeightInUnits;

		if (this.#camera.isOrthographicCamera) {
			canvasWidthInUnits = this.#camera.right - this.#camera.left;
			canvasHeightInUnits = this.#camera.top - this.#camera.bottom;
		} else {
			throw new Error('Camera must be an OrthographicCamera');
		}

		return {
			width: canvasWidthInUnits,
			height: canvasHeightInUnits,
		};
	}

	/**
	 * applyTexture
	 * Apply and scale material texture
	 * @param {Object} mesh
	 * @param {THREE.Texture} texture
	 * @param {number} twidth
	 * @param {number} theight
	 * @param {number} fitaspect
	 * @returns {void}
	 * @private
	 */
	#applyTexture(mesh, texture, twidth, theight, fitaspect) {
		if (!texture) {
			console.warn('Invalid texture: texture is null or undefined');
			return;
		}

		try {
			// Ensure texture is properly initialized
			if (!texture.image) {
				console.warn('Texture image not loaded yet');
				return;
			}

			texture.flipY = false;
			texture.colorSpace = SRGBColorSpace;
			texture.wrapS = RepeatWrapping;
			// texture.wrapS = ClampToEdgeWrapping;
			// texture.wrapT = ClampToEdgeWrapping;
			// texture.generateMipmaps = false;
			texture.repeat.x = -1;

			// Fixes blurry image textures
			if (texture.image.width > 0) {
				texture.minFilter = LinearFilter;
				texture.magFilter = LinearFilter;
			}

			const aspect = twidth / theight;

			if (fitaspect > aspect) {
				texture.repeat.y = aspect / fitaspect;
				texture.offset.y = (1 - texture.repeat.y) / 2;
			} else if (fitaspect < aspect) {
				texture.repeat.x = (fitaspect / aspect) * -1;
				texture.offset.x = (1 - texture.repeat.x) / 2;
			}

			// mesh.current.scale.x *= -1;
			mesh.current.material.map = texture;
			mesh.current.material.transparent = true;
			mesh.current.material.needsUpdate = true;
			mesh.current.material.opacity = 0;

			// Initialize texture in renderer
			this.#renderer.initTexture(texture);

			// gsap.to(mesh.current.material, {
			// 	opacity: 1,
			// 	duration: 0.25,
			// 	ease: 'linear',
			// });
		} catch (error) {
			console.warn('Error applying texture:', error);
		}
	}

	#setupIntersectionObserver() {
		this.#observer = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				const sceneEl = entry.target;

				if (entry.isIntersecting) {
					this.showScene(sceneEl);
				} else {
					this.hideScene(sceneEl);
				}
			});
		}, this.#observerOptions);
	}

	/**
	 * init
	 * Initialize all scene elements
	 * @returns {void}
	 * @public
	 */
	init({ node }) {
		const sceneEls = node.querySelectorAll(this.#options.sceneSelector);
		this.#sceneEls = Array.from(sceneEls);

		// Instead of immediately creating meshes, observe the elements
		this.#sceneEls.forEach(el => {
			this.#observer.observe(el);
		});
	}

	showScene(el) {
		const assets = el.querySelectorAll(this.#options.assetSelector);

		if (!assets.length) return false;

		if (el.classList.contains('has-rendered')) {
			assets.forEach(asset => this.showMeshByElement(asset));
			return false;
		}

		// Scene doesn't yet exist so create it.
		if (!this.#sceneEls.find(e => e === el)) {
			this.#sceneEls.push(el);
		}

		assets.forEach(asset => {
			this.#createMesh(asset);
		});

		el.classList.add('has-rendered');
		// stop observing once initially rendered
		this.#observer.unobserve(el);
	}

	hideScene(el) {
		const assets = el.querySelectorAll(this.#options.assetSelector);
		assets.forEach(asset => {
			this.hideMeshByElement(asset);
		});
	}

	/**
	 * createMesh
	 * Refactored: Handles asset mesh creation by delegating to helpers for video, image, or fill.
	 * @param {HTMLElement} asset
	 * @private
	 */
	#createMesh(asset) {
		if (!asset) return;

		if (asset.tagName === 'VIDEO') {
			this.#createVideoMesh(asset);
		} else if (asset.tagName === 'IMG') {
			this.#createImageMesh(asset);
		} else if (asset.hasAttribute(this.#options.fillSelector)) {
			this.#createFillMesh(asset);
		}
	}

	/**
	 * Helper for creating a mesh for a video asset, including poster handling.
	 * @param {HTMLVideoElement} asset
	 * @private
	 */
	#createVideoMesh(asset) {
		const specs = this.#getElementSpecsNormalized(asset);
		const geometry = this._sharedGeometry.clone();
		const material = this._sharedMaterials.video.clone();
		const videoMeshObj = {
			current: new Mesh(geometry, material),
			domElement: asset,
			type: 'video',
			_cleanup: [],
		};

		const elAspect = asset.clientWidth / asset.clientHeight;

		// Poster mesh logic
		const posterContainer = asset.parentNode ?? null;
		const posterImage =
			posterContainer?.querySelector('.video-poster-image img') ?? null;

		// if (
		// 	posterImage &&
		// 	(asset.readyState <= 2 || asset.hasAttribute('data-play-on-hover'))
		// ) {
		// 	console.log('setup poster image');
		// 	this.#setupPosterMesh(videoMeshObj, posterImage, specs, elAspect);
		// } else {
		// 	console.log('no poster image');
		// }

		// Video texture logic
		const applyVideoTexture = () => {
			const texture = new VideoTexture(asset);
			const { videoWidth: width, videoHeight: height } = texture.source.data;
			this.#applyTexture(videoMeshObj, texture, width, height, elAspect);

			if (!asset.hasAttribute('data-play-on-hover')) {
				asset.play();

				gsap.to(videoMeshObj.current.material, {
					opacity: 1,
					duration: 0.3,
					delay: 0.25,
					ease: 'linear',
				});
			}

			this.#setMeshSpecs(videoMeshObj, ...specs);
			this.#scene.add(videoMeshObj.current);
			this.#meshes.push(videoMeshObj);
		};

		if (asset.readyState >= 3) {
			applyVideoTexture();
		} else {
			if (
				posterImage &&
				(asset.readyState < 2 || asset.hasAttribute('data-play-on-hover'))
			) {
				this.#setupPosterMesh(videoMeshObj, posterImage, specs, elAspect);
			} else {
				console.log('no poster image');
			}

			const loadedHandler = () => {
				applyVideoTexture();

				asset.removeEventListener('canplay', loadedHandler);
			};
			asset.addEventListener('canplay', loadedHandler);
			videoMeshObj._cleanup.push(() =>
				asset.removeEventListener('canplay', loadedHandler)
			);
		}
	}

	/**
	 * Helper for creating a mesh for an image asset.
	 * @param {HTMLImageElement} asset
	 * @private
	 */
	#createImageMesh(asset) {
		const specs = this.#getElementSpecsNormalized(asset);
		const geometry = this._sharedGeometry.clone();
		const material = this._sharedMaterials.image.clone();
		const meshObj = {
			current: new Mesh(geometry, material),
			domElement: asset,
			type: 'image',
			_cleanup: [],
		};

		const elAspect = asset.clientWidth / asset.clientHeight;

		const loadTexture = () => {
			const src = asset.currentSrc || asset.src;
			if (!src) return;
			const loader = new TextureLoader();
			loader.load(
				src,
				texture => {
					const { naturalWidth: width, naturalHeight: height } = asset;
					this.#applyTexture(meshObj, texture, width, height, elAspect);

					gsap.to(meshObj.current.material, {
						opacity: 1,
						duration: 0.25,
						ease: 'linear',
					});
				},
				undefined,
				error => {
					console.error('Error loading texture:', error);
				}
			);
		};

		if (asset.complete && !asset.classList.contains('lazyload')) {
			loadTexture();
		} else {
			asset.addEventListener('lazyloaded', loadTexture);
			meshObj._cleanup.push(() =>
				asset.removeEventListener('lazyloaded', loadTexture)
			);
		}

		const mutationObserver = new MutationObserver(mutations => {
			for (const mutation of mutations) {
				if (
					mutation.type === 'attributes' &&
					['src', 'srcset'].includes(mutation.attributeName)
				) {
					setTimeout(loadTexture, 100);
				}
			}
		});
		mutationObserver.observe(asset, {
			attributes: true,
			attributeFilter: ['src', 'srcset'],
		});
		meshObj._cleanup.push(() => mutationObserver.disconnect());

		// Window resize
		// const resizeHandler = () => setTimeout(loadTexture, 100);
		// window.addEventListener('resize', resizeHandler);
		// meshObj._cleanup.push(() =>
		// 	window.removeEventListener('resize', resizeHandler)
		// );

		this.#setMeshSpecs(meshObj, ...specs);
		this.#scene.add(meshObj.current);
		this.#meshes.push(meshObj);
	}

	/**
	 * Helper for creating a mesh for a fill asset.
	 * @param {HTMLElement} asset
	 * @private
	 */
	#createFillMesh(asset) {
		const specs = this.#getElementSpecsNormalized(asset);
		const geometry = this._sharedGeometry.clone();
		const material = this._sharedMaterials.fill.clone();
		material.color = new Color(asset.dataset.sceneFill);
		const meshObj = {
			current: new Mesh(geometry, material),
			domElement: asset,
			type: 'fill',
			_cleanup: [],
		};

		gsap.to(meshObj.current.material, {
			opacity: 1,
			duration: 0.25,
			ease: 'linear',
		});

		this.#setMeshSpecs(meshObj, ...specs);
		this.#scene.add(meshObj.current);
		this.#meshes.push(meshObj);
	}

	/**
	 * Helper for setting up a poster mesh for a video asset.
	 * @param {HTMLVideoElement} videoAsset
	 * @param {HTMLImageElement} posterImage
	 * @param {Array<number>} specs
	 * @param {number} elAspect
	 * @private
	 */
	#setupPosterMesh(videoMeshObj, posterImage, specs, elAspect) {
		const meshObj = {
			current: null,
			domElement: posterImage,
			type: 'poster',
			_cleanup: [],
		};

		const updateTexture = e => {
			const src = posterImage.currentSrc || posterImage.src;
			if (!src) return;
			const loader = new TextureLoader();
			loader.load(src, texture => {
				const { naturalWidth: width, naturalHeight: height } = posterImage;

				// if by now video is already playing, don't do anything
				if (videoMeshObj.domElement.readyState >= 3) return false;

				if (!meshObj.current) {
					const geometry = new PlaneGeometry(1, 1);
					const material = new MeshBasicMaterial({
						map: texture,
						transparent: true,
						opacity: 1,
						side: DoubleSide,
						depthWrite: false,
					});
					meshObj.current = new Mesh(geometry, material);
					// meshObj.current.position.z = 0.001;
					this.#applyTexture(meshObj, texture, width, height, elAspect);
					this.#setMeshSpecs(meshObj, ...specs);
					this.#scene.add(meshObj.current);
					this.#meshes.push(meshObj);

					gsap.to(meshObj.current.material, {
						opacity: 1,
						duration: 0.25,
						ease: 'linear',
					});
				} else {
					this.#applyTexture(meshObj, texture, width, height, elAspect);

					gsap.to(meshObj.current.material, {
						opacity: 1,
						duration: 0.25,
						ease: 'linear',
					});
				}
			});
		};

		/*
		 * Initial load - Added check to make sure image wasn't set to .lazyload
		 * it believes the placeholder SVG is the loaded image and uses blank texture.
		 */
		if (
			posterImage.complete &&
			posterImage.naturalWidth > 0 &&
			!posterImage.classList.contains('lazyload')
		) {
			updateTexture();
		} else {
			posterImage.addEventListener('lazyloaded', updateTexture, { once: true });
			meshObj._cleanup.push(() =>
				posterImage.removeEventListener('lazyloaded', updateTexture, {
					once: true,
				})
			);
		}

		// MutationObserver for src/srcset/sizes
		const mutationObserver = new MutationObserver(mutations => {
			for (const mutation of mutations) {
				if (
					mutation.type === 'attributes' &&
					['src', 'srcset'].includes(mutation.attributeName)
				) {
					setTimeout(updateTexture, 100);
				}
			}
		});
		mutationObserver.observe(posterImage, {
			attributes: true,
			attributeFilter: ['src', 'srcset'],
		});
		meshObj._cleanup.push(() => mutationObserver.disconnect());

		// Window resize
		// const resizeHandler = () => setTimeout(updateTexture, 100);
		// window.addEventListener('resize', resizeHandler);
		// meshObj._cleanup.push(() =>
		// 	window.removeEventListener('resize', resizeHandler)
		// );

		// Play/pause event listeners for fade in/out
		const posterMaterial = () => meshObj.current?.material;
		const videoMaterial = () => videoMeshObj.current?.material;
		const fadeOut = () => {
			if (posterMaterial()) {
				gsap.to(posterMaterial(), {
					opacity: 0,
					delay: videoMeshObj.domElement.hasAttribute('data-play-on-hover')
						? 0
						: 0.1,
					duration: 0.25,
					overwrite: true,
					onComplete: () => {
						meshObj.current.visible = false;
					},
				});

				gsap.to(videoMaterial(), {
					opacity: 1,
					duration: 0.1,
					overwrite: true,
					onComplete: () => {
						videoMeshObj.current.visible = true;
					},
				});
			}
		};
		const fadeIn = () => {
			if (posterMaterial()) {
				gsap.to(posterMaterial(), {
					opacity: 1,
					duration: 0.25,
					overwrite: true,
					onStart: () => {
						meshObj.current.visible = true;
					},
				});

				gsap.to(videoMaterial(), {
					opacity: 0,
					duration: 0.1,
					delay: 0.15,
					overwrite: true,
					onComplete: () => {
						videoMeshObj.current.visible = false;
					},
				});
			}
		};

		const playHandler = fadeOut;
		const pauseHandler = () => {
			if (videoMeshObj.domElement.hasAttribute('data-play-on-hover')) fadeIn();
		};

		videoMeshObj.domElement.addEventListener('playing', playHandler);
		videoMeshObj.domElement.addEventListener('pause', pauseHandler);
		videoMeshObj._cleanup.push(() =>
			videoMeshObj.domElement.removeEventListener('playing', playHandler)
		);
		videoMeshObj._cleanup.push(() =>
			videoMeshObj.domElement.removeEventListener('pause', pauseHandler)
		);

		// Initial state
		if (!videoMeshObj.domElement.paused) fadeOut();
		else fadeIn();

		return meshObj;
	}

	/**
	 * destroy
	 * Destroy / dispose all resources
	 * @returns {void}
	 * @public
	 */
	destroy() {
		this.cleanupObserver();
		this.#observer.disconnect();

		// Clean up meshes properly
		this.#meshes.forEach(mesh => {
			this.destroyMesh(mesh);
		});

		this.#scene.clear();
		this.#renderer.renderLists.dispose();

		// Not sure this was necessary? - Yeah; for the persistent assets like  in Footer
		this.#sceneEls.forEach(el => {
			// Re-show original assets now
			el.classList.remove('has-rendered');
		});

		this.#sceneEls = [];
		this.#meshes = [];
	}

	/**
	 * destroyMesh
	 * Destroy the mesh associated with a DOM element
	 * @param {Object} mesh
	 * @returns {void}
	 * @public
	 */
	destroyMesh(mesh) {
		if (!mesh) return;

		this.#scene.remove(mesh.current);

		// Call all cleanup functions
		if (mesh._cleanup && Array.isArray(mesh._cleanup)) {
			mesh._cleanup.forEach(fn => {
				try {
					fn();
				} catch (e) {}
			});
		}

		mesh.current?.material?.map?.dispose();
		mesh.current?.geometry?.dispose();
		mesh.current?.material?.dispose();
		this.#meshes.splice(this.#meshes.indexOf(mesh), 1);
	}

	hideMeshByElement(el) {
		const mesh = this.#meshes.find(m => m.domElement === el);
		if (!mesh) return;

		mesh.current.visible = false;
	}

	showMeshByElement(el) {
		const mesh = this.#meshes.find(m => m.domElement === el);
		if (!mesh) return;

		mesh.current.visible = true;
	}

	observe(el) {
		this.#observer.observe(el);
	}

	unobserve(el) {
		this.#observer.unobserve(el);
	}

	cleanupObserver() {
		if (this.#observer) {
			this.#sceneEls.forEach(el => {
				this.#observer.unobserve(el);
			});
			// this.#observer.disconnect();
			// console.log('cleanupObserver disconnected', this.#observer);
		}
	}

	/**
	 * positionSceneElements
	 * Position scene meshes based on position of DOM elements
	 * @returns {void}
	 * @private
	 */
	#positionSceneElements() {
		if (!this.#meshes.length) return;

		// 1) read all layouts first
		const layoutData = this.#meshes.map(mesh => {
			const specs = this.#getElementSpecsNormalized(mesh.domElement);
			return { mesh, specs };
		});

		// 2) then apply transforms
		layoutData.forEach(({ mesh, specs }) => {
			this.#setMeshSpecs(mesh, ...specs);
		});

		// this.#meshes.forEach(mesh => {
		// 	if (mesh && mesh.domElement && mesh.current.visible) {
		// 		mesh.specs = this.#getElementSpecsNormalized(mesh.domElement);
		// 		this.#setMeshSpecs(mesh, ...mesh.specs);
		// 	}
		// });
	}

	/**
	 * setupEventListeners
	 * Setup event listeners
	 * @returns {void}
	 * @private
	 */
	#setupEventListeners() {
		// const onResize = (() => {
		// 	let id;
		// 	return () => {
		// 		clearTimeout(id);
		// 		id = setTimeout(() => this.#handleResize(), 100);
		// 	};
		// })();

		window.addEventListener('resize', () => {
			this.#handleResize();
		});
	}

	/**
	 * handleResize
	 * Handle resize
	 * @returns {void}
	 * @private
	 */
	#handleResize() {
		this.#sizes.width = window.innerWidth;
		this.#sizes.height = window.innerHeight;
		this.#positionSceneElements();
		this.#renderer.setSize(this.#sizes.width, this.#sizes.height);
		this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.composer.setSize(this.#sizes.width, this.#sizes.height);

		// Update the effect's size uniforms
		if (this.#curveEffect) {
			this.#curveEffect.setSize(this.#sizes.width, this.#sizes.height);
		}
	}

	/**
	 * Update canvas position based on settings
	 */
	// updateCanvasPosition() {
	// 	this._setCanvasY(window.lenis.scroll);
	// }

	/**
	 * tick
	 * Request animation frame loop
	 * @returns {void}
	 * @private
	 */
	#tick(time) {
		// for (const callback of this.#callbacks) {
		// 	callback(time);
		// }

		this.#positionSceneElements();

		this.composer.render();

		// Log mesh and scene counts (throttle if needed)
		// if (time % 60 < 1) {
		// 	// log roughly once per second
		// 	console.log(
		// 		`[MudCurveExperience] Meshes: ${this.#meshes.length}, Scenes: ${this.#sceneEls.length}`
		// 	);
		// }
	}

	/**
	 * start
	 * Start the experience
	 * @returns {void}
	 * @public
	 */
	start() {
		// this.#tick();

		Tempus.add(
			time => {
				this.#tick(time);
			},
			{
				priority: 1,
				fps: 60,
			}
		);

		this.init({ node: document.body });

		this.#canvas.classList.remove('hidden');
	}

	/**
	 * onTick
	 * Add a callback to execute on each tick
	 * @param {Function} callback
	 * @returns {void}
	 * @public
	 */
	// onTick(callback) {
	// 	if (typeof callback !== 'function') {
	// 		throw new Error('Callback must be a function');
	// 	}

	// 	this.#callbacks.push(callback);
	// }
}

export default MudCurveExperience;
